import { ArrowLeft, ExternalLink, LogOut, MessageSquare, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router';
import { Logo } from '../ui/Logo';
import { useParent } from './ParentContext';

export function About() {
  const { parent, signOut } = useParent();
  return (
    <div className="page narrow">
      <div className="page-head">
        <div>
          <h1>About</h1>
          <p className="muted">Signed in as {parent.email}</p>
        </div>
        <Link to="/settings" className="icon-btn" aria-label="Back to settings">
          <ArrowLeft size={20} />
        </Link>
      </div>

      <section className="card stack about-hero">
        <Logo large />
        <p>
          <strong>ClassGuardian</strong> keeps homeschool and virtual-school time focused and safe, with your kids always able
          to see when you’re watching or talking. Visible supervision, not spyware.
        </p>
        <p className="muted small">Version {__APP_VERSION__}</p>
      </section>

      <section className="card stack">
        <h2>Learn more</h2>
        <Link to="/help" className="btn">
          Help & how-to
        </Link>
        <Link to="/feedback" className="btn">
          <MessageSquare size={16} /> Send feedback
        </Link>
        <a className="btn" href="https://github.com/jdvieira/ClassGuardian#readme" target="_blank" rel="noreferrer">
          <ShieldCheck size={16} /> Privacy & how it works <ExternalLink size={14} />
        </a>
      </section>

      <button className="btn wide danger-text" onClick={signOut}>
        <LogOut size={18} /> Sign out
      </button>
    </div>
  );
}

/**
 * The how-to part of the Help page (#531): setting up a child's computer, with small screenshots (#528), and everyday
 * tips. Contact us and your requests to support sit around it on the same page.
 */
export function HelpGuide() {
  return (
    <>
      <section className="card stack help-setup">
        <h2>Set up a child’s computer</h2>
        <ol className="help-steps">
          <li>
            <div>
              <strong>Get a Family code.</strong> Open your child, then <strong>Student → Devices → Pair a new computer</strong>. Download the Windows or
              Mac app there, or use the browser page.
            </div>
            <img src="/help/pair.jpg" alt="The Devices tab with a 6-digit Family code" loading="lazy" />
          </li>
          <li>
            <div>
              <strong>On your child’s computer,</strong> install and open the app (or open the browser page) and enter the Family code. Sit with your
              child so you can explain what ClassGuardian shows them.
            </div>
            <img src="/help/agent-code.jpg" alt="The pairing page asking for the Family code" loading="lazy" />
          </li>
          <li>
            <div>
              <strong>That’s it.</strong> During school time your child has School Resources in the corner of their screen: their planner, help, breaks
              and chat. Add the browser extension too for website blocking and the website log.
            </div>
            <img src="/tour/desktop.jpg" alt="School Resources on a child’s desktop" loading="lazy" />
          </li>
        </ol>
      </section>

      <section className="card stack">
        <h2>Everyday</h2>
        <ul className="help-list">
          <li>
            <strong>School hours</strong> (per child) decide when monitoring is on. “School now” and “No school” override the schedule; a break pauses
            everything.
          </li>
          <li>
            <strong>Watch or talk</strong> only works during school time — and your child sees a notice when you do, unless you’ve turned on Hidden mode.
          </li>
          <li>
            <strong>Share a family</strong> with another parent in Settings → Family and Sharing.
          </li>
          <li>
            <strong>Two-factor</strong> and other account options are in Settings → Account security.
          </li>
        </ul>
      </section>
    </>
  );
}
